/**
 * Re-export of the shared useSessionSecurity hook from @cieda/shared.
 *
 * This thin wrapper preserves the original per-app API (all options optional,
 * with sensible defaults) while delegating to the canonical shared
 * implementation. The shared hook requires `onWarning` and
 * `onWarningDismissed` callbacks that this wrapper provides as no-ops when
 * the caller omits them.
 */
import {
  useSessionSecurity as useSessionSecurityShared,
  type SessionSecurityConfig,
  type SessionSecurityReturn,
} from '@cieda/shared';

// ─── Types (preserved for backward compatibility) ────────────────────────────

export interface SessionSecurityOptions {
  /** Idle timeout in milliseconds before logout. Default: 15 min */
  readonly idleTimeoutMs?: number;
  /** Absolute session lifetime from mount. Default: 8 hrs */
  readonly absoluteTimeoutMs?: number;
  /** Time before idle-logout to show the warning. Default: 2 min */
  readonly warningBeforeMs?: number;
  /** Called when session expires (idle or absolute). */
  readonly onLogout?: () => void | Promise<void>;
  /** Optional server-side session validation on tab focus. */
  readonly validateSession?: () => boolean | Promise<boolean>;
  /** Enable / disable the hook entirely. Default: true */
  readonly enabled?: boolean;
}

export interface SessionSecurityResult {
  /** Whether the warning dialog should be visible. */
  readonly isWarningVisible: boolean;
  /** Seconds remaining before logout (counts down during warning). */
  readonly remainingSeconds: number;
  /** Extend the session — resets idle timer and hides warning. */
  readonly extendSession: () => void;
  /** Immediately log out and broadcast to other tabs. */
  readonly forceLogout: () => void;
}

// ─── Defaults ────────────────────────────────────────────────────────────────

const DEFAULT_IDLE_MS = 15 * 60 * 1_000; // 15 min
const DEFAULT_WARN_MS = 2 * 60 * 1_000; // 2 min
const DEFAULT_ABS_MS = 8 * 60 * 60 * 1_000; // 8 hrs

const noop = () => {};
const noopAsync = async () => true;

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useSessionSecurity(
  options: SessionSecurityOptions = {},
): SessionSecurityResult {
  const {
    idleTimeoutMs = DEFAULT_IDLE_MS,
    absoluteTimeoutMs = DEFAULT_ABS_MS,
    warningBeforeMs = DEFAULT_WARN_MS,
    onLogout,
    validateSession,
    enabled = true,
  } = options;

  const config: SessionSecurityConfig = {
    idleTimeoutMs,
    absoluteTimeoutMs,
    warningBeforeMs,
    onLogout: onLogout ?? noop,
    onWarning: noop,
    onWarningDismissed: noop,
    validateSession: validateSession
      ? () => Promise.resolve(validateSession())
      : noopAsync,
    enabled,
  };

  return useSessionSecurityShared(config);
}
